import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Client } from '@gigdriver/domain';
import {
  getOrgClients,
  createClient as createClientApi,
  updateClient as updateClientApi,
  deleteClient as deleteClientApi,
  importInvoiceNinjaClients,
  pushClientToInvoiceNinja,
} from '@gigdriver/client';

export const useClientsStore = defineStore('clients', () => {
  const clients = ref<Client[]>([]);
  const isLoading = ref(false);
  const isImporting = ref(false);
  const pushingId = ref<string | null>(null);
  const error = ref<string | null>(null);

  async function loadOrgClients(orgId: string) {
    try {
      isLoading.value = true;
      error.value = null;
      clients.value = await getOrgClients(orgId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load clients';
    } finally {
      isLoading.value = false;
    }
  }

  async function createClient(
    orgId: string,
    data: Omit<Client, 'id' | 'orgId' | 'createdAt' | 'updatedAt'>,
  ) {
    try {
      error.value = null;
      const clientId = await createClientApi(orgId, data);
      await loadOrgClients(orgId);
      return clientId;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to add client';
      throw err;
    }
  }

  async function updateClient(orgId: string, clientId: string, updates: Partial<Client>) {
    try {
      error.value = null;
      await updateClientApi(orgId, clientId, updates);
      await loadOrgClients(orgId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update client';
      throw err;
    }
  }

  async function deleteClient(orgId: string, clientId: string) {
    try {
      error.value = null;
      await deleteClientApi(orgId, clientId);
      clients.value = clients.value.filter(c => c.id !== clientId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete client';
      throw err;
    }
  }

  async function importFromInvoiceNinja(orgId: string) {
    try {
      isImporting.value = true;
      error.value = null;
      const result = await importInvoiceNinjaClients({ orgId });
      await loadOrgClients(orgId);
      return result;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to import from Invoice Ninja';
      throw err;
    } finally {
      isImporting.value = false;
    }
  }

  async function pushToInvoiceNinja(orgId: string, clientId: string) {
    try {
      pushingId.value = clientId;
      error.value = null;
      const result = await pushClientToInvoiceNinja({ orgId, clientId });
      await loadOrgClients(orgId);
      return result;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to push to Invoice Ninja';
      throw err;
    } finally {
      pushingId.value = null;
    }
  }

  return {
    clients,
    isLoading,
    isImporting,
    pushingId,
    error,
    loadOrgClients,
    createClient,
    updateClient,
    deleteClient,
    importFromInvoiceNinja,
    pushToInvoiceNinja,
  };
});
